import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components';
import { Cloud, CloudSun, Sun } from 'lucide-react-native';
import theme from '@/constants/colors';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';

type Props = {
  trail: any;
  setTrail: (updater: any) => void;
};

type ChipLevel = 'low' | 'mid' | 'high' | null;
type PathWidth = 'wide' | 'narrow' | null;

// 1-5 scale → 0-100 range (values land exactly on trafficLevelFromValue thresholds
// so the stored value decodes back to the tapped level; 50 stays legacy "mid")
const TRAFFIC_MAP: Record<number, number> = { 1: 10, 2: 30, 3: 50, 4: 70, 5: 100 };
const TRAFFIC_LABELS = ['quiet', 'light', 'some', 'busy', 'lots'];

// Legacy 3-level map (for backward compat with review screen)
const LEVEL_MAP: Record<string, number> = { low: 25, mid: 50, high: 75 };
const PATH_MAP: Record<string, number> = { wide: 25, narrow: 75 };

function trafficLevelFromValue(value: number | undefined): number {
  if (value === undefined || value === null) return 3; // default to middle
  if (value <= 10) return 1;
  if (value <= 30) return 2;
  if (value <= 50) return 3;
  if (value <= 70) return 4;
  return 5;
}

function levelFromValue(value: number | undefined): ChipLevel {
  if (value === undefined || value === null) return null;
  if (value <= 35) return 'low';
  if (value >= 65) return 'high';
  return 'mid';
}

function pathFromValue(value: number | undefined): PathWidth {
  if (value === undefined || value === null) return null;
  if (value <= 40) return 'wide';
  return 'narrow';
}

function exposureFromValue(value: number | undefined): ChipLevel {
  if (value === undefined || value === null) return null;
  if (value <= 35) return 'low';
  if (value >= 65) return 'high';
  return 'mid';
}

export default function TrailTraits({ trail, setTrail }: Props) {
  const { t } = useTranslation();
  if (!trail) return null;

  const patch = (key: string, value: any) => {
    setTrail((prev: any) => ({ ...prev, [key]: value }));
  };

  const dogTrafficLevel = trafficLevelFromValue(trail.dogTraffic);
  const footTrafficLevel = trafficLevelFromValue(trail.footTraffic);
  const exposureLevel = exposureFromValue(trail.exposure);
  const pathWidth = pathFromValue(trail.paths);

  const selectDogTraffic = (level: number) => {
    Haptics.selectionAsync();
    if (dogTrafficLevel === level) return;
    patch('dogTraffic', TRAFFIC_MAP[level]);
  };

  const selectFootTraffic = (level: number) => {
    Haptics.selectionAsync();
    if (footTrafficLevel === level) return;
    patch('footTraffic', TRAFFIC_MAP[level]);
  };

  const selectExposure = (level: ChipLevel) => {
    Haptics.selectionAsync();
    if (exposureLevel === level) return;
    patch('exposure', LEVEL_MAP[level!]);
  };

  const selectPaths = (width: PathWidth) => {
    Haptics.selectionAsync();
    if (pathWidth === width) return;
    patch('paths', PATH_MAP[width!]);
  };

  return (
    <View style={{ marginTop: 16 }}>

      {/* Dog traffic — 1-5 buttons */}
      <Text style={traitStyles.label}>{t('trailEdit.dogsAround')}</Text>
      <View style={traitStyles.buttonRow}>
        {[1, 2, 3, 4, 5].map((level) => (
          <TouchableOpacity
            key={level}
            onPress={() => selectDogTraffic(level)}
            style={[traitStyles.trafficButton, dogTrafficLevel === level && traitStyles.trafficButtonSelected]}
            activeOpacity={0.7}
          >
            <Text style={[traitStyles.trafficButtonNumber, dogTrafficLevel === level && traitStyles.trafficButtonNumberSelected]}>
              {level}
            </Text>
            <Text style={[traitStyles.trafficButtonLabel, dogTrafficLevel === level && traitStyles.trafficButtonLabelSelected]}>
              {t(`filters.${TRAFFIC_LABELS[level - 1]}`)}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Foot traffic — 1-5 buttons */}
      <Text style={[traitStyles.label, { marginTop: 16 }]}>{t('trailEdit.peopleAround')}</Text>
      <View style={traitStyles.buttonRow}>
        {[1, 2, 3, 4, 5].map((level) => (
          <TouchableOpacity
            key={level}
            onPress={() => selectFootTraffic(level)}
            style={[traitStyles.trafficButton, footTrafficLevel === level && traitStyles.trafficButtonSelected]}
            activeOpacity={0.7}
          >
            <Text style={[traitStyles.trafficButtonNumber, footTrafficLevel === level && traitStyles.trafficButtonNumberSelected]}>
              {level}
            </Text>
            <Text style={[traitStyles.trafficButtonLabel, footTrafficLevel === level && traitStyles.trafficButtonLabelSelected]}>
              {t(`filters.${TRAFFIC_LABELS[level - 1]}`)}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Exposure */}
      <Text style={[traitStyles.label, { marginTop: 16 }]}>{t('trailEdit.exposure')}</Text>
      <View style={traitStyles.chipRow}>
        <TouchableOpacity onPress={() => selectExposure('low')} style={[traitStyles.chip, exposureLevel === 'low' && traitStyles.chipSelected]}>
          <Cloud size={18} color={exposureLevel === 'low' ? theme.backgroundPrimary : theme.accentPrimary} />
        </TouchableOpacity>
        <TouchableOpacity onPress={() => selectExposure('mid')} style={[traitStyles.chip, exposureLevel === 'mid' && traitStyles.chipSelected]}>
          <CloudSun size={18} color={exposureLevel === 'mid' ? theme.backgroundPrimary : theme.accentPrimary} />
        </TouchableOpacity>
        <TouchableOpacity onPress={() => selectExposure('high')} style={[traitStyles.chip, exposureLevel === 'high' && traitStyles.chipSelected]}>
          <Sun size={18} color={exposureLevel === 'high' ? theme.backgroundPrimary : theme.accentPrimary} />
        </TouchableOpacity>
      </View>

      {/* Paths */}
      <Text style={[traitStyles.label, { marginTop: 16 }]}>{t('trailEdit.paths')}</Text>
      <View style={traitStyles.chipRow}>
        <TouchableOpacity onPress={() => selectPaths('wide')} style={[traitStyles.chip, pathWidth === 'wide' && traitStyles.chipSelected]}>
          <Text style={[traitStyles.chipText, pathWidth === 'wide' && traitStyles.chipTextSelected]}>{t('trailEdit.wideOpen')}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => selectPaths('narrow')} style={[traitStyles.chip, pathWidth === 'narrow' && traitStyles.chipSelected]}>
          <Text style={[traitStyles.chipText, pathWidth === 'narrow' && traitStyles.chipTextSelected]}>{t('trailEdit.narrow')}</Text>
        </TouchableOpacity>
      </View>

      {/* Off-leash */}
      <Text style={[traitStyles.label, { marginTop: 16 }]}>{t('trailEdit.offLeashFriendly')}</Text>
      <View style={traitStyles.chipRow}>
        <TouchableOpacity onPress={() => { Haptics.selectionAsync(); patch('offLeash', true); }} style={[traitStyles.chip, !!trail.offLeash && traitStyles.chipSelected]}>
          <Text style={[traitStyles.chipText, !!trail.offLeash && traitStyles.chipTextSelected]}>{t('common.yes')}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => { Haptics.selectionAsync(); patch('offLeash', false); }} style={[traitStyles.chip, !trail.offLeash && traitStyles.chipSelected]}>
          <Text style={[traitStyles.chipText, !trail.offLeash && traitStyles.chipTextSelected]}>{t('common.no')}</Text>
        </TouchableOpacity>
      </View>

      {/* Wildlife */}
      <Text style={[traitStyles.label, { marginTop: 16 }]}>{t('trailEdit.wildlifeLivestock')}</Text>
      <View style={traitStyles.chipRow}>
        <TouchableOpacity onPress={() => { Haptics.selectionAsync(); patch('wildlife', true); }} style={[traitStyles.chip, !!trail.wildlife && traitStyles.chipSelected]}>
          <Text style={[traitStyles.chipText, !!trail.wildlife && traitStyles.chipTextSelected]}>{t('common.yes')}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => { Haptics.selectionAsync(); patch('wildlife', false); }} style={[traitStyles.chip, !trail.wildlife && traitStyles.chipSelected]}>
          <Text style={[traitStyles.chipText, !trail.wildlife && traitStyles.chipTextSelected]}>{t('common.no')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const traitStyles = StyleSheet.create({
  label: {
    color: theme.textMuted,
    fontSize: 14,
    marginBottom: 8,
  },
  chipRow: {
    flexDirection: 'row',
    gap: 10,
  },
  chip: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: '#282E10',
    borderWidth: 1.5,
    borderColor: '#3d4520',
  },
  chipSelected: {
    backgroundColor: 'rgba(255, 254, 119, 0.15)',
    borderColor: theme.accentPrimary,
  },
  chipText: {
    color: theme.accentPrimary,
    fontSize: 14,
    fontWeight: '500',
  },
  chipTextSelected: {
    fontWeight: '700',
  },

  // Traffic 1-5 buttons
  buttonRow: {
    flexDirection: 'row',
    gap: 8,
  },
  trafficButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 12,
    backgroundColor: '#282E10',
    borderWidth: 1.5,
    borderColor: '#3d4520',
  },
  trafficButtonSelected: {
    backgroundColor: 'rgba(255, 254, 119, 0.15)',
    borderColor: theme.accentPrimary,
  },
  trafficButtonNumber: {
    color: theme.accentPrimary,
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 22,
  },
  trafficButtonNumberSelected: {
    color: theme.accentPrimary,
  },
  trafficButtonLabel: {
    color: theme.textMuted,
    fontSize: 10,
    fontWeight: '500',
    marginTop: 2,
  },
  trafficButtonLabelSelected: {
    color: theme.accentPrimary,
  },
});